import type { CSSProperties } from 'react';
import { AiPanel, Typography } from '@all-manager/design-system-v2';
import type { InboxEmail } from '../../types/inbox.types';
import { type SummaryBlock, useDocumentSummaryCard } from './useDocumentSummaryCard';

type Props = {
  summary?: string | null;
  email?: InboxEmail | null;
};

// Native list: DS Typography cannot render as `ul`/`li`.
const LIST_STYLE: CSSProperties = {
  margin: 0,
  paddingInlineStart: '1.25em',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

function renderInline(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function renderBlock(block: SummaryBlock, index: number) {
  if (block.type === 'heading') {
    return (
      <Typography key={index} variant="h6">
        {renderInline(block.text)}
      </Typography>
    );
  }

  if (block.type === 'subheading') {
    return (
      <Typography key={index} variant="subtitle1">
        {renderInline(block.text)}
      </Typography>
    );
  }

  if (block.type === 'paragraph') {
    return (
      <Typography key={index} variant="body2">
        {renderInline(block.text)}
      </Typography>
    );
  }

  const items = block.items.map((item, itemIndex) => (
    <li key={itemIndex}>
      <Typography variant="body2">{renderInline(item)}</Typography>
    </li>
  ));

  return block.type === 'orderedList' ? (
    <ol key={index} style={LIST_STYLE}>{items}</ol>
  ) : (
    <ul key={index} style={LIST_STYLE}>{items}</ul>
  );
}

export default function DocumentSummaryCard(props: Props) {
  const hook = useDocumentSummaryCard(props);

  if (!hook.hasSummary) {
    return null;
  }

  return (
    <AiPanel title="Compte rendu du mail et des documents">
      {hook.blocks.map(renderBlock)}
    </AiPanel>
  );
}
